import { camelToKebab } from "@/common/helpers/strings";
import DOMPurify from "dompurify";
const baseConfigParams = {
    ALLOW_DATA_ATTR: false,
    ALLOWED_ATTR: [],
    IN_PLACE: true,
    SPECIAL_TREAT: ["trim"]
    // Note that SAFE_FOR_TEMPLATES: true is not included here because the regex can hang under certain payloads
};
const configsMap = new Map([
    [
        "preview",
        {
            ...baseConfigParams,
            ALLOWED_TAGS: ["strong", "em", "i", "span", "s", "b", "p", "del"],
            ALLOWED_ATTR: ["style"],
            ALLOWED_STYLES: ["color", "backgroundColor", "fontFamily", "fontWeight"],
            TRANSFORMED_TAGS: { li: "p", td: "p" }
        }
    ],
    [
        "text",
        {
            ...baseConfigParams,
            KEEP_CONTENT: true,
            ALLOWED_TAGS: ["#text"]
        }
    ],
    [
        "tooltip",
        {
            ...baseConfigParams,
            ALLOWED_ATTR: ["class"]
        }
    ],
    [
        // Used for redactor source html mode (codemirror plugin)
        "htmleditor",
        {
            ALLOW_DATA_ATTR: true,
            ALLOW_ARIA_ATTR: true,
            ALLOWED_TAGS: [
                "a",
                "blockquote",
                "br",
                "button",
                "cite",
                "code",
                "div",
                "figure",
                "h1",
                "h2",
                "h3",
                "h4",
                "h5",
                "h6",
                "i",
                "img",
                "input",
                "kbd",
                "label",
                "li",
                "mark",
                "ol",
                "p",
                "pre",
                "samp",
                "section",
                "span",
                "strong",
                "sub",
                "sup",
                "table",
                "tbody",
                "td",
                "th",
                "thead",
                "tr",
                "ul",
                "var",
                "video",
                "piped-value",
                "piping-selector"
            ],
            ALLOWED_ATTR: [
                "alt",
                "class",
                "contenteditable",
                "disabled",
                "href",
                "id",
                "inert",
                "name",
                "placeholder",
                "readonly",
                "rel",
                "role",
                "src",
                "style",
                "tabindex",
                "target",
                "title",
                "type"
            ],
            IN_PLACE: false
        }
    ]
]);
/*
 * This function is used to replace tags with their transformed counterparts.
 * Use config property TRANSFORMED_TAGS to provide a mapping of tags to be replaced. The key is the tag
 * to be replaced and the value is the tag to replace it with.
 *
 * Example:
 * const config = { TRANSFORMED_TAGS: { li: "p", td: "p" } };
 * replaceTags("<li>Item 1</li><td>Cell 1</td>", config); // "<p>Item 1</p><p>Cell 1</p>"
 */
const replaceTags = (sanitized, config) => {
    if (!sanitized.length)
        return sanitized;
    const { TRANSFORMED_TAGS } = config;
    const transformedTags = Object.entries(TRANSFORMED_TAGS ?? {});
    if (!transformedTags.length)
        return sanitized;
    return transformedTags.reduce((acc, [key, value]) => {
        const pattern = new RegExp(`(</*)${key}([^>]*)`, "g");
        return acc.replace(pattern, (_, p1, p2) => {
            return `${p1}${value}${p2}`;
        });
    }, sanitized);
};
/*
 * This function is used to filter inline styles based on the ALLOWED_STYLES config property.
 * Use config property ALLOWED_STYLES to provide a list of styles to be allowed and ensure that "style"
 * is included in config property ALLOWED_ATTR.
 *
 * Example:
 * const config = { ALLOWED_ATTR: ["style"], ALLOWED_STYLES: ["color"] };
 * filterStyles('<p style="color: red; background-color: blue;">Text</p>', config); // '<p style="color: red;">Text</p>'
 */
const filterStyles = (sanitized, config) => {
    if (!sanitized.length)
        return sanitized;
    const { ALLOWED_ATTR, ALLOWED_STYLES } = config;
    if (!(ALLOWED_ATTR ?? []).includes("style") || !ALLOWED_STYLES)
        return sanitized;
    const attrPattern = /style="([^"]*)"/g;
    const allowedStylesSet = new Set(ALLOWED_STYLES.map((style) => camelToKebab(String(style))));
    const inlineFilteredStyles = sanitized
        .match(attrPattern)?.[0]
        .replace(/style=|"/g, "")
        .split(";")
        .reduce((acc, style) => {
        const trimmed = style.trim();
        if (trimmed.length) {
            const [key, value] = trimmed.split(":").map((part, i) => (!i ? camelToKebab(part.trim()) : part.trim()));
            if (allowedStylesSet.has(key))
                return [...acc, `${key}: ${value}`];
        }
        return acc;
    }, [])
        .join("; ");
    return sanitized.replace(attrPattern, inlineFilteredStyles?.length ? `style="${inlineFilteredStyles}"` : "");
};
const filterClasses = (sanitized, config) => {
    const { ALLOWED_CLASS } = config;
    if (!sanitized.length || !ALLOWED_CLASS?.length)
        return sanitized;
    const attrPattern = /class="([^"]*)"/g;
    return sanitized.replace(attrPattern, (_, p1) => `class="${p1
        .split(/\s+/)
        .filter((c) => ALLOWED_CLASS.includes(c))
        .join(" ")}"`);
};
const customValidation = (sanitized, config) => {
    const { CUSTOM_SANITIZER } = config;
    if (!sanitized.length || !CUSTOM_SANITIZER)
        return sanitized;
    return CUSTOM_SANITIZER(sanitized);
};
/*
 * This function is used to pass a partially-sanitized/non-sanitized string to DOMPurify for further sanitization.
 * Use config property to provide a custom configuration object.
 * (DOMPurify documentation available at https://github.com/cure53/DOMPurify )
 */
const purify = (sanitized, config) => String(DOMPurify.sanitize(sanitized, config));
/*
 * This function will be the last one to alter the sanitized string. Use it to add extra processing.
 * As of now, it only supports trimming the string.
 * Use config property SPECIAL_TREAT to provide a list of special treatments.
 *
 * Example:
 * const config = { SPECIAL_TREAT: ["trim"] };
 * specialTreat("  Text  ", config); // "Text"
 */
const specialTreat = (sanitized, config) => {
    const { SPECIAL_TREAT } = config;
    if (!sanitized.length || !SPECIAL_TREAT)
        return sanitized;
    let tmp = sanitized;
    SPECIAL_TREAT.forEach((treat) => {
        switch (treat) {
            case "trim":
                tmp = sanitized.trim();
                break;
            default:
        }
    });
    return tmp;
};
/*
 * Main function to sanitize the input string.
 * Refer to the AugmentedConfig interface declaration to create a custom configuration. Optionally,
 * provide a config name to use a predefined configuration (preview, text, tooltip). Default configuration
 * is baseConfigParams.
 */
const sanitizeHtml = (dirty, configName) => {
    const config = configName ? configsMap.get(configName) : baseConfigParams;
    return [replaceTags, filterClasses, filterStyles, customValidation, purify, specialTreat].reduce((input, fn) => fn(input, config), dirty);
};
export { baseConfigParams, configsMap, replaceTags, filterStyles, purify, specialTreat, sanitizeHtml };
